import { BaseScreenProps } from '../types'
import ScreenTitle from '@/features/quiz/screen-title'
import React from 'react'
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import BottomContinueButton from '@/features/quiz/bottom-continue-button'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { CreatePaymentLink } from '@/lib/apis/treatments'
import { isPaymentAlreadyReceivedError } from '@/lib/apis/patient/orders'
import { useStore } from '@/lib/store'
import {
  NotificationItem,
  NotificationItemVariant,
} from '@/lib/models/NotificationItem'
import { PricingValue } from '@/lib/cms/pricing'
import {
  RewardedPrice,
  RewardSaving,
  rewardId,
} from '@/features/rewards/web-loyalty-reward'

type Props = {} & BaseScreenProps

export default function MhlServiceSelectorScreen({
  treatmentType,
  rewardResponse,
}: Props) {
  const client = useApiClient()
  const router = useRouter()
  const paymentLinkMutation = useMutation(
    CreatePaymentLink.mutation(client, treatmentType),
  )

  const showNotification = useStore((state) => state.showNotification)

  return (
    <>
      <div className="mb-16 w-full">
        <div className="mx-auto max-w-3xl">
          <div className="space-y-8">
            <ScreenTitle title="Review your consultation pricing" />
            <div className="space-y-10 border-y border-gray-200 bg-white py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-base font-medium text-gray-900">
                    Initial consultation
                  </p>
                  <p className="text-sm text-gray-600">
                    E-scripts will be sent to you once approved
                  </p>
                  <RewardSaving response={rewardResponse} />
                </div>
                <span className="font-psans text-lg font-semibold text-rich-black">
                  <RewardedPrice
                    response={rewardResponse}
                    fallback={
                      <PricingValue name="treatmentInitialConsultation" />
                    }
                  />
                </span>
              </div>

              <p className="text-sm text-blue-800">
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  className="mr-1.5"
                />
                If we find that our treatments aren{"'"}t a good fit for you, we
                {"'"}ll refund your initial consultation fee.
              </p>
            </div>
          </div>
        </div>
      </div>

      <BottomContinueButton
        onClick={() => {
          paymentLinkMutation.mutate(
            { rewardId: rewardId(rewardResponse) },
            {
              onSuccess: (data) => {
                window.location.assign(data.checkoutUrl)
              },
              onError: (error) => {
                // The earlier checkout was paid, so retrying here would only fail again.
                // The portal shows the request's progress.
                if (isPaymentAlreadyReceivedError(error)) {
                  showNotification(
                    new NotificationItem(
                      'Payment already received.',
                      NotificationItemVariant.Success,
                    ),
                  )
                  router.push('/portal')
                  return
                }

                showNotification(
                  new NotificationItem(
                    'Something went wrong. Please try again later',
                    NotificationItemVariant.Error,
                  ),
                )
              },
            },
          )
        }}
        initial={true}
        show={true}
        buttonProps={{
          children: 'Continue to payment',
          loading: paymentLinkMutation.isPending,
        }}
      />
    </>
  )
}
